import React, { useState } from 'react';
import { ApiError, fieldError, toApiError } from '../api/problem';
import { useI18n } from '../i18n/I18nProvider';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/Toasts';

const MIN_PASSWORD_LENGTH = 8;

export function AccountPage() {
  const { user, changePassword } = useAuth();
  const { t } = useI18n();
  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirm) {
      setMismatch(true);
      return;
    }

    setMismatch(false);
    setBusy(true);

    try {
      await changePassword(currentPassword, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      showToast(t('account.changed'), { tone: 'success' });
    } catch (caught) {
      setError(toApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-card">
      <h1 className="h4 mb-1">{t('account.title')}</h1>
      <p className="text-body-secondary small">{user?.email}</p>

      {error && <div className="alert alert-danger py-2">{error.message}</div>}

      <form onSubmit={submit} noValidate>
        <div className="mb-3">
          <label className="form-label" htmlFor="current-password">
            {t('account.currentPassword')}
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            className={`form-control ${fieldError(error, 'currentPassword') ? 'is-invalid' : ''}`}
            value={currentPassword}
            onChange={event => setCurrentPassword(event.target.value)}
            required
          />
          <div className="invalid-feedback">{fieldError(error, 'currentPassword')}</div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="new-password">
            {t('account.newPassword')}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            className={`form-control ${fieldError(error, 'newPassword') ? 'is-invalid' : ''}`}
            value={newPassword}
            onChange={event => setNewPassword(event.target.value)}
            required
          />
          <div className="form-text">{t('auth.passwordHint', { count: MIN_PASSWORD_LENGTH })}</div>
          <div className="invalid-feedback">{fieldError(error, 'newPassword')}</div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="confirm-password">
            {t('account.confirmNew')}
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            className={`form-control ${mismatch ? 'is-invalid' : ''}`}
            value={confirm}
            onChange={event => setConfirm(event.target.value)}
            required
          />
          <div className="invalid-feedback">{t('auth.mismatch')}</div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('common.saving') : t('account.submit')}
        </button>
      </form>
    </div>
  );
}
